import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { format, startOfMonth, endOfMonth } from 'date-fns';
import {
  Layers,
  DollarSign,
  BarChart3,
  CalendarDays,
  Hotel,
  LogIn,
  LogOut,
  Loader2,
  Info,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useAuth } from '@/hooks/useAuth';
import { usePropertyContext } from '@/contexts/PropertyContext';
import { usePortfolio } from '@/hooks/api/usePortfolio';
import type {
  PortfolioCurrencyBucket,
  PortfolioPropertyRow,
} from '@/services/api/portfolio.api';

// ─── Helpers ────────────────────────────────────────────────────────────────

function fmtMoney(currency: string, value: number): string {
  return `${currency} ${value.toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  })}`;
}

function fmtMoney2(currency: string, value: number): string {
  return `${currency} ${value.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

function tenantNameFromEmail(email: string | null | undefined): string {
  const domain = email?.split('@')[1]?.split('.')[0];
  if (!domain) return 'Portfolio';
  return domain.charAt(0).toUpperCase() + domain.slice(1);
}

// ─── Currency chip ────────────────────────────────────────────────────────────

function CurrencyChip({
  currency,
  text,
  active,
  onClick,
}: {
  currency: string;
  text: string;
  active?: boolean;
  onClick?: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active ?? false}
      aria-label={`${currency}: ${text}`}
      className={cn(
        'inline-flex items-center rounded-full border px-2.5 py-0.5 text-sm font-semibold transition-colors',
        active
          ? 'border-primary-500 bg-primary-50 text-primary-700'
          : 'border-gray-200 bg-gray-50 text-gray-800 hover:bg-gray-100',
      )}
    >
      {text}
    </button>
  );
}

// ─── State badge ──────────────────────────────────────────────────────────────

function StateBadge({ state }: { state: PortfolioPropertyRow['state'] }) {
  const cfg =
    state === 'live'
      ? { label: 'Live', dot: 'bg-green-500', badge: 'bg-green-100 text-green-800' }
      : { label: 'Draft', dot: 'bg-amber-500', badge: 'bg-amber-100 text-amber-800' };
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium',
        cfg.badge,
      )}
    >
      <span className={cn('h-1.5 w-1.5 rounded-full', cfg.dot)} aria-hidden="true" />
      {cfg.label}
    </span>
  );
}

// ─── KPI tile (per-currency chips, never blind-summed) ────────────────────────

function CurrencyKpiTile({
  label,
  icon,
  iconBg,
  buckets,
  render,
  activeCurrency,
  onChipClick,
}: {
  label: string;
  icon: React.ReactNode;
  iconBg: string;
  buckets: PortfolioCurrencyBucket[];
  render: (b: PortfolioCurrencyBucket) => string;
  activeCurrency: string | null;
  onChipClick: (currency: string) => void;
}) {
  const mixed = buckets.length > 1;
  return (
    <div className="rounded-xl border border-gray-200 bg-white p-5">
      <div className="mb-3 flex items-center justify-between">
        <p className="text-sm font-medium text-gray-500">{label}</p>
        <div className={cn('shrink-0 rounded-lg p-2', iconBg)} aria-hidden="true">
          {icon}
        </div>
      </div>
      {buckets.length === 0 ? (
        <p className="text-2xl font-bold text-gray-300">—</p>
      ) : (
        <div className="flex flex-wrap items-center gap-1.5">
          {buckets.map((b) => (
            <CurrencyChip
              key={b.currency}
              currency={b.currency}
              text={render(b)}
              active={activeCurrency === b.currency}
              onClick={() => onChipClick(b.currency)}
            />
          ))}
          {mixed && (
            <span
              className="inline-flex items-center gap-0.5 text-[11px] text-gray-400"
              title="Values are kept per currency and never summed across currencies."
            >
              <Info size={12} aria-hidden="true" />
              mixed
            </span>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Date range ────────────────────────────────────────────────────────────────

function DateRangeSelector({
  from,
  to,
  onChange,
}: {
  from: string;
  to: string;
  onChange: (from: string, to: string) => void;
}) {
  return (
    <div className="flex items-center gap-2">
      <label htmlFor="pf-from" className="sr-only">
        From date
      </label>
      <input
        id="pf-from"
        type="date"
        value={from}
        onChange={(e) => onChange(e.target.value, to)}
        className="rounded-lg border border-gray-300 px-3 py-1.5 text-sm text-gray-700 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500"
        aria-label="Portfolio start date"
      />
      <span className="text-sm text-gray-400">to</span>
      <label htmlFor="pf-to" className="sr-only">
        To date
      </label>
      <input
        id="pf-to"
        type="date"
        value={to}
        onChange={(e) => onChange(from, e.target.value)}
        className="rounded-lg border border-gray-300 px-3 py-1.5 text-sm text-gray-700 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500"
        aria-label="Portfolio end date"
      />
    </div>
  );
}

// ─── Portfolio dashboard ──────────────────────────────────────────────────────

export function PortfolioDashboard() {
  const { user } = useAuth();
  const { selectProperty } = usePropertyContext();
  const navigate = useNavigate();

  const [dateRange, setDateRange] = useState<{ from: string; to: string }>(() => {
    const now = new Date();
    return {
      from: format(startOfMonth(now), 'yyyy-MM-dd'),
      to: format(endOfMonth(now), 'yyyy-MM-dd'),
    };
  });
  const [currencyFilter, setCurrencyFilter] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [rows, setRows] = useState<PortfolioPropertyRow[]>([]);

  const query = useMemo(
    () => ({
      from: dateRange.from,
      to: dateRange.to,
      page,
      limit: 20,
      ...(currencyFilter ? { currency: currencyFilter } : {}),
    }),
    [dateRange.from, dateRange.to, page, currencyFilter],
  );

  const { data, isLoading, isError, isFetching, refetch } = usePortfolio(query);

  // Reset paging whenever the window or currency filter changes.
  useEffect(() => {
    setPage(1);
    setRows([]);
  }, [dateRange.from, dateRange.to, currencyFilter]);

  // Accumulate drill-down rows across pages (dedupe by id).
  useEffect(() => {
    if (!data) return;
    const incoming = data.properties ?? [];
    setRows((prev) => {
      if (page === 1) return incoming;
      const seen = new Set(prev.map((r) => r.id));
      return [...prev, ...incoming.filter((r) => !seen.has(r.id))];
    });
  }, [data, page]);

  const tenantName = tenantNameFromEmail(user?.email);
  const buckets = data?.currencyBuckets ?? [];
  const hasMore = data ? (data.meta?.page ?? 1) < (data.meta?.totalPages ?? 1) : false;

  function handleChipClick(currency: string) {
    setCurrencyFilter((cur) => (cur === currency ? null : currency));
  }

  function handleRowClick(row: PortfolioPropertyRow) {
    selectProperty({ id: row.id, name: row.name });
    navigate('/dashboard');
  }

  // ─── Loading (first load) ──────────────────────────────────────────────────
  if (isLoading && !data) {
    return (
      <div className="space-y-6">
        <div className="h-8 w-64 animate-pulse rounded bg-gray-100" />
        <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="h-28 animate-pulse rounded-xl bg-gray-100" />
          ))}
        </div>
        <div className="h-64 animate-pulse rounded-xl bg-gray-100" />
      </div>
    );
  }

  // ─── Error ─────────────────────────────────────────────────────────────────
  if (isError && !data) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-center">
        <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-red-100">
          <span className="text-xl font-bold text-red-600">!</span>
        </div>
        <h2 className="mb-2 text-lg font-semibold text-gray-900">Couldn't load portfolio</h2>
        <button
          type="button"
          onClick={() => refetch()}
          className="inline-flex items-center gap-2 rounded-lg bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700"
        >
          Retry
        </button>
      </div>
    );
  }

  const propertyCount = data?.propertyCount ?? 0;
  const live = data?.live ?? 0;
  const draft = data?.draft ?? 0;
  const excluded = data?.excluded?.draftOrNoData ?? 0;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 text-xl font-semibold text-gray-900">
            <Layers size={20} className="text-primary-600" aria-hidden="true" />
            Portfolio — {tenantName}
          </h1>
          <p className="mt-0.5 text-sm text-gray-500">
            {propertyCount} {propertyCount === 1 ? 'property' : 'properties'} · {live} live ·{' '}
            {draft} draft
          </p>
        </div>
        <DateRangeSelector
          from={dateRange.from}
          to={dateRange.to}
          onChange={(from, to) => setDateRange({ from, to })}
        />
      </div>

      {/* Empty */}
      {propertyCount === 0 ? (
        <div className="rounded-xl border border-gray-200 bg-white py-16 text-center">
          <Layers size={40} className="mx-auto mb-3 text-gray-300" aria-hidden="true" />
          <p className="text-sm text-gray-500">No activity yet across your portfolio.</p>
        </div>
      ) : (
        <>
          {/* Per-currency KPI tiles — never blind-summed */}
          <section aria-label="Portfolio key performance indicators">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <CurrencyKpiTile
                label="Revenue (MTD)"
                icon={<DollarSign size={16} className="text-emerald-600" />}
                iconBg="bg-emerald-50"
                buckets={buckets}
                render={(b) => fmtMoney(b.currency, b.revenue)}
                activeCurrency={currencyFilter}
                onChipClick={handleChipClick}
              />
              <CurrencyKpiTile
                label="Occupancy"
                icon={<BarChart3 size={16} className="text-blue-600" />}
                iconBg="bg-blue-50"
                buckets={buckets}
                render={(b) => `${b.currency} ${b.occupancyRate.toFixed(1)}%`}
                activeCurrency={currencyFilter}
                onChipClick={handleChipClick}
              />
              <CurrencyKpiTile
                label="ADR"
                icon={<CalendarDays size={16} className="text-purple-600" />}
                iconBg="bg-purple-50"
                buckets={buckets}
                render={(b) => fmtMoney2(b.currency, b.adr)}
                activeCurrency={currencyFilter}
                onChipClick={handleChipClick}
              />
              <CurrencyKpiTile
                label="RevPAR"
                icon={<Hotel size={16} className="text-orange-600" />}
                iconBg="bg-orange-50"
                buckets={buckets}
                render={(b) => fmtMoney2(b.currency, b.revPar)}
                activeCurrency={currencyFilter}
                onChipClick={handleChipClick}
              />
            </div>
          </section>

          {/* Today across the portfolio (per-currency arrivals/departures) */}
          <section aria-label="Portfolio today">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <CurrencyKpiTile
                label="Arrivals today"
                icon={<LogIn size={16} className="text-green-600" />}
                iconBg="bg-green-50"
                buckets={buckets}
                render={(b) => `${b.currency} ${b.arrivalsToday}`}
                activeCurrency={currencyFilter}
                onChipClick={handleChipClick}
              />
              <CurrencyKpiTile
                label="Departures today"
                icon={<LogOut size={16} className="text-orange-600" />}
                iconBg="bg-orange-50"
                buckets={buckets}
                render={(b) => `${b.currency} ${b.departuresToday}`}
                activeCurrency={currencyFilter}
                onChipClick={handleChipClick}
              />
            </div>
          </section>

          {/* Drill-down table */}
          <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
            <div className="flex items-center justify-between gap-2 border-b border-gray-100 px-5 py-4">
              <h2 className="text-base font-semibold text-gray-900">Properties</h2>
              {currencyFilter && (
                <button
                  type="button"
                  onClick={() => setCurrencyFilter(null)}
                  className="text-xs font-medium text-primary-600 hover:text-primary-700"
                >
                  Clear {currencyFilter} filter
                </button>
              )}
            </div>

            <div className="overflow-x-auto">
              <table className="w-full min-w-[720px] text-sm">
                <thead>
                  <tr className="border-b border-gray-100 text-left text-xs uppercase tracking-wide text-gray-400">
                    <th scope="col" className="px-5 py-2.5 font-medium">Property</th>
                    <th scope="col" className="px-3 py-2.5 font-medium">State</th>
                    <th scope="col" className="px-3 py-2.5 text-right font-medium">Occ%</th>
                    <th scope="col" className="px-3 py-2.5 text-right font-medium">Arr</th>
                    <th scope="col" className="px-3 py-2.5 text-right font-medium">Dep</th>
                    <th scope="col" className="px-3 py-2.5 text-right font-medium">Revenue</th>
                    <th scope="col" className="px-3 py-2.5 text-right font-medium">ADR</th>
                    <th scope="col" className="px-3 py-2.5 font-medium">Ccy</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {rows.map((row) => (
                    <tr
                      key={row.id}
                      onClick={() => handleRowClick(row)}
                      tabIndex={0}
                      role="button"
                      aria-label={`Open ${row.name} dashboard`}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          handleRowClick(row);
                        }
                      }}
                      className="cursor-pointer transition-colors hover:bg-gray-50 focus:bg-gray-50 focus:outline-none"
                    >
                      <td className="px-5 py-3 font-medium text-gray-900">{row.name}</td>
                      <td className="px-3 py-3">
                        <StateBadge state={row.state} />
                      </td>
                      <td className="px-3 py-3 text-right text-gray-700">
                        {row.occupancyRate.toFixed(1)}%
                      </td>
                      <td className="px-3 py-3 text-right text-gray-700">{row.arrivals}</td>
                      <td className="px-3 py-3 text-right text-gray-700">{row.departures}</td>
                      <td className="px-3 py-3 text-right text-gray-700">
                        {fmtMoney(row.currency, row.revenueMtd)}
                      </td>
                      <td className="px-3 py-3 text-right text-gray-700">
                        {fmtMoney2(row.currency, row.adr)}
                      </td>
                      <td className="px-3 py-3 text-gray-500">{row.currency}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Footer — excluded footnote + load more */}
            <div className="flex items-center justify-between gap-2 border-t border-gray-100 px-5 py-3">
              <span className="text-xs text-gray-400">
                {excluded > 0
                  ? `${excluded} ${excluded === 1 ? 'property' : 'properties'} excluded from currency weighting (draft / no data)`
                  : `${data?.meta?.total ?? rows.length} total`}
              </span>
              {hasMore && (
                <button
                  type="button"
                  onClick={() => setPage((p) => p + 1)}
                  disabled={isFetching}
                  className="inline-flex items-center gap-1 text-xs font-medium text-primary-600 hover:text-primary-700 disabled:opacity-50"
                >
                  {isFetching && <Loader2 size={12} className="animate-spin" aria-hidden="true" />}
                  Load more
                </button>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
